import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faCalculator, 
  faArrowLeft, 
  faArrowRight, 
  faCheck, 
  faBuilding, 
  faRulerCombined, 
  faStairs, 
  faMoneyCheckAlt,
  faTree, 
  faCalendarAlt, 
  faWrench, 
  faComment, 
  faEnvelope,
  faPhone,
  faUmbrellaBeach,
  faEllipsisH,
  faStar,
  faTools,
  faTimes,
  faKey,
  faCheckDouble,
  faCalendarCheck,
  faMinus,
  faUserTie,
  faDollarSign
} from '@fortawesome/free-solid-svg-icons';
import axios from '../../axios';
import config from '../../config';
import { trackFormSubmission, getValuationInputFormType, getSourceDomain, getValuationMarket, isBodexEsHost } from '../../utils/analytics';
import { getStoredTrackingParams } from '../../utils/tracking';
import Spinner from '../Spinner/spinner';
import SpinnerOverlay from '../Spinner/spinneroverlay';
import { useLanguage } from '../../i18n/LanguageContext';
import { translatePropertyType } from '../../i18n/translations';
import { getMapboxCountry, getMapboxLanguage } from '../../utils/mapboxCountry';
import './ValuationChat.css';

const cleanPhoneNumber = (value) => String(value || '').replace(/[\s\-()]/g, '');

const isLikelyPhoneNumber = (value) => {
  const cleanPhone = cleanPhoneNumber(value);
  return /^\+\d{8,15}$/.test(cleanPhone) || /^\d{7,15}$/.test(cleanPhone);
};

// Translate a backend validation error response into the user's UI language
// using the stable `error_code` returned by the API. Falls back to the server
// message when no translation is registered for the code (e.g. on legacy
// deployments). Returns null when there's nothing usable in `data`.
const localizeBackendError = (data, questionId, t) => {
  if (!data) return null;
  const code = data.error_code;
  const meta = data.error_meta || {};

  // useLanguage's t() returns the key itself when missing — treat that as a miss.
  const lookup = (key, values) => {
    const out = t(key, values);
    return typeof out === 'string' && out !== key ? out : null;
  };

  if (code) {
    // Resolve {country} into a localized country name; pass other meta values
    // through unchanged so t()'s interpolate() can substitute them.
    const values = { ...meta };
    if (meta.country) {
      const iso = String(meta.country).toUpperCase();
      values.country = lookup(`valuationChat.countries.${iso}`) || iso;
    }

    const buckets =
      questionId === 'email'
        ? ['valuationChat.emailErrors', 'valuationChat.phoneErrors']
        : ['valuationChat.phoneErrors', 'valuationChat.emailErrors'];

    for (const bucket of buckets) {
      const translated = lookup(`${bucket}.${code}`, values);
      if (translated) return translated;
    }
  }

  return data.error || null;
};

const ValuationChat = ({ address }) => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [loadingValuation, setLoadingValuation] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionsAnswers, setQuestionsAnswers] = useState([]);
  const [valuationId, setValuationId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Form state for answers
  const [answers, setAnswers] = useState({
    propertyType: '',
    numberOfRooms: '',
    area: '',
    floor: '',
    monthlyFee: '',
    plotSize: '',
    constructionYear: '',
    condition: '',
    additionalInfo: '',
    ownership: '',
    phone: '',
    email: '',
    latitude: null,
    longitude: null,
    lan: '',
    acceptsTerms: true, // Default to true (checked)
    gclid: '',
    wbraid: '',
    gbraid: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    language,
    // Bodex site the visitor is on (e.g. "bodex.se", "bodex.es", "www.bodex.es").
    // Forwarded to the backend with all_answers so the Lead row records which
    // domain initiated the valuation and the offline Google Ads conversion can
    // be routed to the per-market conversion action.
    source_domain: getSourceDomain(),
    valuation_market: getValuationMarket()
  });
  
  // Prevent double initialization
  const initializationRef = useRef(false);
  const valuationSubmittedRef = useRef(false);
  const sliderDragRef = useRef({ isNearThumb: false, savedValue: null });
  const [trackingData, setTrackingData] = useState({});

  
  const history = useHistory();

  useEffect(() => {
    setAnswers(prev => ({
      ...prev,
      language,
      source_domain: getSourceDomain(),
      valuation_market: getValuationMarket()
    }));
  }, [language]);

  // Helper to anonymize address by stripping house number
  const anonymizedAddress = address ? address.replace(/\s+\d+.*$/, '') : '';

  // Helper to extract just the street name from full address
  const getStreetName = (fullAddress) => {
    if (!fullAddress) return '';
    // Extract just the street name (before first comma or postal code)
    const streetPart = fullAddress.split(',')[0].trim();
    return streetPart;
  };

  // Function to track individual user input
  const trackIndividualInput = async (questionId, answer, pageSource) => {
    try {
      // Format the answer with its question context for the address field
      const formattedAnswer = `${questionId}: ${answer}`;
      
      await axios.post(`${config.backend_url}/api/track-user-input`, {
        address: formattedAnswer,
        email: questionId === 'email' ? answer : null,
        page_source: pageSource
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('User input tracking failed:', error);
    }
  };

  // Predefined questions with UI components and icons
  const predefinedQuestions = [
    {
      id: 'propertyType',
      question: t('valuationChat.questions.propertyType'),
      icon: faBuilding,
      type: 'select',
      options: [
        { value: '', label: t('valuationChat.placeholders.propertyType') },
        { value: 'Lägenhet', label: translatePropertyType('Lägenhet', language) },
        { value: 'Villa', label: translatePropertyType('Villa', language) },
        { value: 'Radhus', label: translatePropertyType('Radhus', language) },
        // Hide "Fritidsboende" and "Övrigt" for the Norwegian variant
        ...(language === 'no'
          ? []
          : [{ value: 'Fritidsboende', label: translatePropertyType('Fritidsboende', language) }]),
        { value: 'Tomt', label: translatePropertyType('Tomt', language) },
        ...(language === 'no'
          ? []
          : [{ value: 'Övrigt', label: translatePropertyType('Övrigt', language) }])
      ],
      required: true
    },
    {
      id: 'numberOfRooms',
      question: t('valuationChat.questions.numberOfRooms'),
      icon: faHome,
      type: 'select',
      options: [
        { value: '', label: t('valuationChat.placeholders.rooms') },
        { value: '1', label: t('valuationChat.roomOptions.one') },
        { value: '2', label: t('valuationChat.roomOptions.two') },
        { value: '3', label: t('valuationChat.roomOptions.three') },
        { value: '4', label: t('valuationChat.roomOptions.four') },
        { value: '5', label: t('valuationChat.roomOptions.five') },
        { value: '6', label: t('valuationChat.roomOptions.sixPlus') }
      ],
      required: true,
      condition: (answers) => answers.propertyType !== 'Tomt'
    },
    {
      id: 'area',
      question: t('valuationChat.questions.area'),
      icon: faRulerCombined,
      type: 'number',
      placeholder: t('valuationChat.placeholders.area'),
      min: 1,
      max: 10000,
      required: true,
      condition: (answers) => answers.propertyType !== 'Tomt'
    },
    {
      id: 'floor',
      question: t('valuationChat.questions.floor'),
      icon: faStairs,
      type: 'number',
      placeholder: t('valuationChat.placeholders.floor'),
      min: 0,
      max: 50,
      required: true,
      condition: (answers) => answers.propertyType === 'Lägenhet'
    },
    {
      id: 'monthlyFee',
      question: t('valuationChat.questions.monthlyFee'),
      icon: faMoneyCheckAlt,
      type: 'number',
      placeholder: t('valuationChat.placeholders.monthlyFee'),
      min: 0,
      max: 100000,
      required: true,
      condition: (answers) => answers.propertyType === 'Lägenhet'
    },
    {
      id: 'plotSize',
      question: t('valuationChat.questions.plotSize'),
      icon: faTree,
      type: 'number',
      placeholder: t('valuationChat.placeholders.plotSize'),
      min: 1,
      max: 1000000,
      required: false,
      condition: (answers) => ['Villa', 'Radhus', 'Fritidsboende', 'Tomt'].includes(answers.propertyType)
    },
    {
      id: 'constructionYear',
      question: t('valuationChat.questions.constructionYear'),
      icon: faCalendarAlt,
      type: 'number',
      placeholder: t('valuationChat.placeholders.constructionYear'),
      min: 1800,
      max: new Date().getFullYear(),
      required: false,
      condition: (answers) => ['Villa', 'Radhus', 'Fritidsboende'].includes(answers.propertyType)
    },
    {
      id: 'condition',
      question: t('valuationChat.questions.condition'),
      icon: faWrench,
      type: 'select',
      options: [
        { value: '', label: t('valuationChat.placeholders.condition') },
        { value: 'Mycket bra', label: t('valuationChat.conditionOptions.veryGood') },
        { value: 'Bra', label: t('valuationChat.conditionOptions.good') },
        { value: 'Normalt', label: t('valuationChat.conditionOptions.normal') },
        { value: 'Behöver renovering', label: t('valuationChat.conditionOptions.renovate') }
      ],
      required: true,
      condition: (answers) => answers.propertyType !== 'Tomt'
    },
    {
      id: 'additionalInfo',
      question: t('valuationChat.questions.additionalInfo'),
      icon: faComment,
      type: 'textarea',
      placeholder: t('valuationChat.placeholders.additionalInfo'),
      required: false
    },
    {
      id: 'ownership',
      question: t('valuationChat.questions.ownership'),
      icon: faKey,
      type: 'select',
      options: [
        { value: '', label: t('common.select') },
        { value: 'Ja', label: t('common.yes') },
        { value: 'Nej', label: t('common.no') }
      ],
      required: true
    },
    {
      id: 'phone',
      question: t('valuationChat.questions.phone'),
      icon: faPhone,
      type: 'phone',
      placeholder: t('valuationChat.placeholders.phone'),
      required: true
    },
    {
      id: 'email',
      question: t('valuationChat.questions.email'),
      icon: faEnvelope,
      type: 'email',
      placeholder: t('valuationChat.placeholders.email'),
      required: true
    }
  ];

  // Get current question
  const getCurrentQuestion = () => {
    const filteredQuestions = predefinedQuestions.filter(q => 
      !q.condition || q.condition(answers)
    );
    return filteredQuestions[currentQuestionIndex] || null;
  };

  // Get total questions count
  const getTotalQuestions = () => {
    return predefinedQuestions.filter(q => 
      !q.condition || q.condition(answers)
    ).length;
  };

  // Initialize valuation when component mounts
  useEffect(() => {
    if (address && !initializationRef.current) {
      initializationRef.current = true;
      initializeValuation();
    }
  }, [address]);

  useEffect(() => {
    const storedTracking = getStoredTrackingParams();
    if (storedTracking && Object.keys(storedTracking).length > 0) {
      setTrackingData(storedTracking);
      setAnswers(prev => ({
        ...prev,
        ...storedTracking
      }));
    }
  }, []);



  // Geocode address to get coordinates and län using Mapbox
  const geocodeAddress = async (addressToGeocode) => {
    try {
      if (!addressToGeocode || !config.mapbox_token) {
        console.warn('Address or Mapbox token missing for geocoding');
        return { latitude: null, longitude: null, lan: '' };
      }

      // Step 1: Forward geocoding to get coordinates
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          addressToGeocode
        )}.json?access_token=${config.mapbox_token}&limit=1&country=${getMapboxCountry(language)}&language=${getMapboxLanguage(language)}`
      );
      
      if (!response.ok) {
        throw new Error(`Geocoding API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        // Mapbox returns [lng, lat]
        const longitude = feature.center[0];
        const latitude = feature.center[1];
        
        // Extract län from forward geocoding context first
        let extractedLan = '';
        if (feature.context) {
          // Look for region context
          for (const context of feature.context) {
            if (context.id && context.id.startsWith('region')) {
              extractedLan = context.text || '';
              // Remove "län" suffix if present and standardize (same as populate_lan_field.py)
              extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
              break;
            }
          }
        }
        
        // Also check if the feature itself is a region
        if (!extractedLan && feature.place_type && feature.place_type.includes('region')) {
          extractedLan = feature.text || '';
          extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
        }
        
        // Step 2: If no län found, try reverse geocoding with coordinates (same as Python script)
        if (!extractedLan) {
          try {
            const reverseResponse = await fetch(
              `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${config.mapbox_token}&country=${getMapboxCountry(language)}&types=region&language=${getMapboxLanguage(language)}`
            );
            
            if (reverseResponse.ok) {
              const reverseData = await reverseResponse.json();
              if (reverseData.features && reverseData.features.length > 0) {
                for (const reverseFeature of reverseData.features) {
                  if (reverseFeature.place_type && reverseFeature.place_type.includes('region')) {
                    extractedLan = reverseFeature.text || '';
                    extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
                    break;
                  }
                }
              }
            }
          } catch (reverseError) {
            console.warn('Reverse geocoding failed:', reverseError);
          }
        }
        
        console.log(`Geocoded address "${addressToGeocode}" to:`, { latitude, longitude, lan: extractedLan });
        return { latitude, longitude, lan: extractedLan };
      } else {
        console.warn('No geocoding results found for address:', addressToGeocode);
        return { latitude: null, longitude: null, lan: '' };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      return { latitude: null, longitude: null, lan: '' };
    }
  };

  // Initialize valuation with the address
  const initializeValuation = async () => {
    if (initializationRef.current && valuationId) {
      console.log('Valuation already initialized');
      return;
    }
    
    setLoading(true);
    try {
      const trackingSnapshot = Object.keys(trackingData).length > 0 ? trackingData : getStoredTrackingParams();
      if (trackingSnapshot && Object.keys(trackingSnapshot).length > 0) {
        setTrackingData(trackingSnapshot);
        setAnswers(prev => ({
          ...prev,
          ...trackingSnapshot
        }));
      }
      
      const response = await axios.post(`${config.backend_url}/api/start-valuation`, {
        address: address,
        tracking: trackingSnapshot || {},
        language,
        // Stored on the valuation session and inherited by the Lead so the
        // backend picks the correct Google Ads conversion action per domain.
        source_domain: getSourceDomain(),
        valuation_market: getValuationMarket()
      });
      
      if (response.data.success) {
        setValuationId(response.data.valuation_id);
        
        // Track valuation start
        trackFormSubmission('valuation_started', {
          address: anonymizedAddress,
          event_category: 'Valuation',
          event_label: 'Started Property Valuation'
        });
        
        // Geocode address to get coordinates and län early in the process
        console.log('Geocoding address for coordinates and län:', address);
        const geocodingResult = await geocodeAddress(address);
        if (geocodingResult.latitude && geocodingResult.longitude) {
          setAnswers(prev => ({
            ...prev,
            latitude: geocodingResult.latitude,
            longitude: geocodingResult.longitude,
            lan: geocodingResult.lan || ''
          }));
          console.log('Successfully added coordinates and län to answers state:', geocodingResult);
        }
      }
    } catch (error) {
      console.error('Error starting valuation:', error);
      setValidationError(t('valuationChat.startError'));
      // Reset initialization flag on error so it can be retried
      initializationRef.current = false;
    } finally {
      setLoading(false);
    }
  };



  // Handle answer change
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    setValidationError('');
  };

  // Haptic feedback for slider interaction
  const triggerHapticFeedback = () => {
    // Check if vibration API is available
    if (navigator.vibrate) {
      // Short vibration (10ms) for subtle haptic feedback
      navigator.vibrate(10);
    }
  };

  // Validate current answer
  const validateCurrentAnswer = () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return { isValid: false, error: t('valuationChat.noQuestion') };
    
    const value = answers[currentQuestion.id];
    
    if (currentQuestion.required && (!value || value.toString().trim() === '')) {
      return { isValid: false, error: t('valuationChat.required') };
    }
    
    // COMMENTED OUT: Special validation for ownership question - must accept terms
    // if (currentQuestion.id === 'ownership' && !answers.acceptsTerms) {
    //   return { isValid: false, error: 'Du måste acceptera Bodex villkor för att fortsätta' };
    // }
    
    if (currentQuestion.type === 'number' && value) {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return { isValid: false, error: t('valuationChat.invalidNumber') };
      }
      if (currentQuestion.min !== undefined && numValue < currentQuestion.min) {
        return { isValid: false, error: t('valuationChat.minValue', { value: currentQuestion.min }) };
      }
      if (currentQuestion.max !== undefined && numValue > currentQuestion.max) {
        return { isValid: false, error: t('valuationChat.maxValue', { value: currentQuestion.max }) };
      }
    }
    
    if (currentQuestion.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return { isValid: false, error: t('valuationChat.invalidEmail') };
      }
    }
    
    if (currentQuestion.type === 'phone' && value) {
      // Keep the client check intentionally broad. The backend normalizes and
      // verifies the country/mobile status with libphonenumber + Twilio.
      if (!isLikelyPhoneNumber(value)) {
        return { isValid: false, error: t('valuationChat.invalidPhone') };
      }
    }
    
    return { isValid: true };
  };

  // Navigate to previous question
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev - 1);
        setValidationError('');
        setIsTransitioning(false);
      }, 150);
    }
  };

  // Save important answers to backend session immediately
  const saveAnswerToBackend = async (questionId, answer) => {
    try {
      // Save phone, email, and ownership to backend session immediately for auto-processing
      if (questionId === 'phone' || questionId === 'email' || questionId === 'ownership') {
        if (!valuationId) {
          console.error(`ERROR: No valuation ID available for saving ${questionId}`);
          return;
        }
        
        const response = await axios.post(`${config.backend_url}/api/update-valuation-session`, {
          valuation_id: valuationId,
          [questionId]: answer,
          // Pass UI language so the backend's get_allowed_phone_countries()
          // reliably switches to multi-country mode (vs Swedish-only).
          language,
          source_domain: getSourceDomain(),
          valuation_market: getValuationMarket()
        });
        
        // Check if validation failed (especially for phone validation)
        if (!response.data.success) {
          const localized = localizeBackendError(response.data, questionId, t);
          const err = new Error(localized);
          err.errorCode = response.data.error_code;
          err.errorMeta = response.data.error_meta;
          throw err;
        }
      }
    } catch (error) {
      console.error(`ERROR: Failed to save ${questionId} to backend:`, error);
      console.error(`ERROR: Response data:`, error.response?.data);
      console.error(`ERROR: Status:`, error.response?.status);
      
      // For phone and email validation errors, throw the error to prevent conversion tracking
      // This ensures we only track conversions for VALID phone numbers
      if (questionId === 'phone' || questionId === 'email') {
        // Prefer translation by error_code from the backend; fall back to the
        // server-provided message, then the generic unexpected-error string.
        const errorMessage =
          localizeBackendError(error.response?.data, questionId, t) ||
          error.message ||
          t('objectForm.validation.unexpected');
        throw new Error(errorMessage);
      }
      // For ownership, don't throw - not critical
    }
  };

  // Submit answer and move to next question or complete valuation
  const handleSubmitAnswer = async () => {
    // Prevent rapid clicking and duplicate submissions
    if (isSubmitting || hasNavigated) {
      console.log('Preventing duplicate submission');
      return;
    }

    const validation = validateCurrentAnswer();
    
    if (!validation.isValid) {
      setValidationError(validation.error);
      return;
    }

    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return;

    const currentAnswer = answers[currentQuestion.id];
    
    // Set submitting state to prevent rapid clicking
    setIsSubmitting(true);
    
    // Format answer for display
    let displayAnswer = currentAnswer;
    if (currentQuestion.type === 'select') {
      const option = currentQuestion.options.find(opt => opt.value === currentAnswer);
      displayAnswer = option ? option.label : currentAnswer;
    } else if (currentQuestion.type === 'number') {
      if (currentQuestion.id === 'monthlyFee') {
        displayAnswer = isBodexEsHost()
          ? `${currentAnswer} €`
          : `${currentAnswer} kr`;
      } else if (currentQuestion.id === 'area' || currentQuestion.id === 'plotSize') {
        displayAnswer = `${currentAnswer} m²`;
      } else if (currentQuestion.id === 'constructionYear') {
        displayAnswer = t('valuationChat.yearValue', { value: currentAnswer });
      } else if (currentQuestion.id === 'floor') {
        displayAnswer = currentAnswer === 0 || currentAnswer === '0'
          ? t('valuationChat.floorGround')
          : t('valuationChat.floorValue', { value: currentAnswer });
      }
    } else if (currentQuestion.type === 'textarea') {
      // Handle textarea type - if no additional info provided, use default message
      if (!currentAnswer || currentAnswer.trim() === '') {
        displayAnswer = t('followUp.finalDefault');
      } else {
        displayAnswer = currentAnswer;
      }
    }

    // Track individual user input in user_tracking table (non-critical tracking)
    trackIndividualInput(currentQuestion.id, currentAnswer, 'valuation_page');
    
    setLoading(true);
    setValidationError('');
    
    try {
      // Save important answers to backend immediately (phone, email and ownership)
      // This includes API validation for phone numbers
      // Will throw error if validation fails (e.g., invalid phone number)
      await saveAnswerToBackend(currentQuestion.id, currentAnswer);
      
      // Calculate conversion value and label
      let conversionValue = 0.2;
      let eventLabel = 'Question Answered';
      
      // COMMENTED OUT: Online conversion tracking replaced with offline conversion via Google Ads API
      // Progressive values now calculated and sent only when lead is marked as correct via email verification
      // Online tracking disabled to prevent double-counting with offline conversions

      // Online conversion tracking for valuation_svara_click with progressive values
      // Track only for qualified leads (ownership: "Ja")
      const isQualifiedOwnership = answers.ownership === 'Ja';
      
      if (currentQuestion.id === 'ownership' && isQualifiedOwnership) {
        // Track ownership conversion: 25 SEK
        conversionValue = 0.25;
        eventLabel = 'Ownership Qualified';
      } else if (currentQuestion.id === 'phone' && isQualifiedOwnership) {
        // Track phone conversion: 100 SEK (only if ownership qualified)
        conversionValue = 0.5;
        eventLabel = 'Phone Entered';
      } else if (currentQuestion.id === 'email' && isQualifiedOwnership && answers.phone) {
        // Track email conversion: 300 SEK (only if ownership qualified AND phone entered)
        conversionValue = 1;
        eventLabel = 'Email Entered';
      }
      

      // Track conversion (single event with value 1 for each step - offline conversion handles final value)
      // Uses `valuation_input_in_form_es` when the visitor is on bodex.es,
      // otherwise the default `valuation_input_in_form`.
      trackFormSubmission(getValuationInputFormType(), {
        conversion_value: conversionValue,
        event_category: 'Valuation',
        event_label: eventLabel
      });
      
      // Add to local Q&A history
      const newQA = {
        question: currentQuestion.question,
        answer: displayAnswer
      };
      setQuestionsAnswers(prev => [...prev, newQA]);
      
      // Check if this is the last question
      const totalQuestions = getTotalQuestions();
      const isLastQuestion = currentQuestionIndex >= totalQuestions - 1;
      
      if (isLastQuestion) {
        // Prevent multiple navigation attempts
        if (hasNavigated || valuationSubmittedRef.current) {
          console.log('Navigation already in progress or valuation already submitted');
          return;
        }
        
        setHasNavigated(true);
        valuationSubmittedRef.current = true;
        setLoadingValuation(true);
        
        // Navigate to ValuationResult with all data - let ValuationResult handle the final submission
        history.push('/valuation-result', {
          valuationId: valuationId,
          address: address,
          questionsAnswers: [...questionsAnswers, newQA],
          phone: answers.phone,
          email: answers.email,
          allAnswers: answers,
          isPending: true, // Let ValuationResult handle the final submission properly
          finalQuestion: currentQuestion.question,
          finalAnswer: displayAnswer,
          finalQuestionCount: currentQuestionIndex + 1
        });
      } else {
        // Move to next question with transition
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentQuestionIndex(prev => prev + 1);
          setIsTransitioning(false);
        }, 150);
        
        // Submit current answer to backend for tracking
        await axios.post(`${config.backend_url}/api/submit-valuation-answer`, {
          valuation_id: valuationId,
          question: currentQuestion.question,
          answer: displayAnswer,
          questions_count: currentQuestionIndex + 1,
          is_final_question: false
        });
        
        // Reset submitting state for non-final questions
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting valuation answer:', error);
      
      // Use specific error message from API validation (e.g., phone validation errors)
      const errorMessage = error.message || error.response?.data?.error || t('valuationChat.submitError');
      setValidationError(errorMessage);
      
      setLoadingValuation(false);
      setIsSubmitting(false);
      setHasNavigated(false);
      valuationSubmittedRef.current = false;
    } finally {
      if (!hasNavigated) {
        setLoading(false);
      }
    }
  };

  // Go back to homepage
  const handleGoHome = () => {
    history.push('/');
  };

  // Calculate progress
  const calculateProgress = () => {
    const totalQuestions = getTotalQuestions();
    return Math.min(((currentQuestionIndex + 1) / totalQuestions) * 100, 100);
  };

  // Render input component based on question type
  const renderInputComponent = (question) => {
    const value = answers[question.id] || '';
    
    switch (question.type) {
      case 'select':
        return (
          <div>
            {/* Add terms checkbox for ownership question - above the options */}
            {/* COMMENTED OUT: Terms checkbox - can be restored later
            {question.id === 'ownership' && (
              <div className="terms-checkbox-container">
                <label className="terms-checkbox-label">
                  <input
                    type="checkbox"
                    className="terms-checkbox"
                    checked={answers.acceptsTerms}
                    onChange={(e) => handleAnswerChange('acceptsTerms', e.target.checked)}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">
                    Jag accepterar Bodex{' '}
                    <a 
                      href="/terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="terms-link"
                    >
                      användarvillkor
                    </a>
                  </span>
                </label>
              </div>
            )}
            */}
            <div className="valuation-option-cards">
              {question.options
                .filter(option => option.value !== '') // Skip the empty default option
                .map(option => (
                  <div
                    key={option.value}
                    className={`valuation-option-card ${value === option.value ? 'selected' : ''}`}
                    onClick={() => handleAnswerChange(question.id, option.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAnswerChange(question.id, option.value);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-pressed={value === option.value}
                    aria-label={`${option.label}${getOptionDescription(question.id, option.value) ? `. ${getOptionDescription(question.id, option.value)}` : ''}`}
                  >
                    <div className="option-icon">
                      {getOptionIcon(question.id, option.value)}
                    </div>
                    <div className="option-content">
                      <div className="option-label">{option.label}</div>
                      {getOptionDescription(question.id, option.value) && (
                        <div className="option-description">{getOptionDescription(question.id, option.value)}</div>
                      )}
                    </div>
                    <div className="option-check">
                      {value === option.value && <FontAwesomeIcon icon={faCheck} />}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        );
      
      case 'number':
        const sliderRange = getRealisticSliderRange(question.id);
        const sliderValue = value && value >= sliderRange.min && value <= sliderRange.max ? value : sliderRange.default;
        
        return (
          <div className="valuation-slider-container">
            <div className="slider-manual-input">
              <input
                type="number"
                className="manual-input"
                value={value}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                placeholder={question.placeholder}
                min={question.min}
                max={question.max}
              />
              <span className="input-unit">{getSliderUnit(question.id)}</span>
            </div>
            <div className="slider-wrapper">
              <input
                type="range"
                className="valuation-slider"
                value={sliderValue}
                onPointerDown={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const ratio = (sliderValue - sliderRange.min) / (sliderRange.max - sliderRange.min);
                  const thumbPos = rect.left + ratio * rect.width;
                  const threshold = 30;
                  const isNear = Math.abs(e.clientX - thumbPos) <= threshold;
                  sliderDragRef.current = { isNearThumb: isNear, savedValue: sliderValue };
                }}
                onPointerUp={() => {
                  sliderDragRef.current = { isNearThumb: false, savedValue: null };
                }}
                onChange={(e) => {
                  if (sliderDragRef.current.isNearThumb) {
                    handleAnswerChange(question.id, e.target.value);
                    triggerHapticFeedback();
                  } else {
                    e.target.value = sliderDragRef.current.savedValue ?? sliderValue;
                  }
                }}
                onInput={(e) => {
                  if (sliderDragRef.current.isNearThumb) {
                    triggerHapticFeedback();
                  }
                }}
                min={sliderRange.min}
                max={sliderRange.max}
                step={getSliderStep(question.id)}
                aria-label={question.question}
                aria-valuemin={sliderRange.min}
                aria-valuemax={sliderRange.max}
                aria-valuenow={sliderValue}
                aria-valuetext={formatSliderValue(question.id, sliderValue)}
              />
              <div className="slider-labels">
                <span className="min-label">{formatSliderValue(question.id, sliderRange.min)}</span>
                <span className="max-label">{formatSliderValue(question.id, sliderRange.max)}</span>
              </div>
            </div>
          </div>
        );
      
      case 'textarea':
        return (
          <textarea
            className="valuation-textarea"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            rows={4}
          />
        );
      
      case 'phone':
        return (
          <input
            type="tel"
            className="valuation-phone-input"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder}
          />
        );

      case 'email':
        return (
          <input
            type="email"
            className="valuation-email-input"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder}
          />
        );
      
      default:
        return (
          <input
            type="text"
            className="valuation-text-input"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder}
          />
        );
    }
  };

  // Helper function to get room layout SVGs
  const getRoomLayoutSVG = (roomCount) => {
    const roomNum = parseInt(roomCount);
    const baseSize = 36;
    const roomSize = 8;
    const gap = 1;

    switch (roomNum) {
      case 1:
        return (
          <svg width={baseSize} height={baseSize} viewBox="0 0 36 36">
            <rect x="14" y="14" width="8" height="8" fill="currentColor" rx="1"/>
          </svg>
        );
      
      case 2:
        return (
          <svg width={baseSize} height={baseSize} viewBox="0 0 36 36">
            <rect x="11" y="14" width="6" height="8" fill="currentColor" rx="1"/>
            <rect x="19" y="14" width="6" height="8" fill="currentColor" rx="1"/>
          </svg>
        );
      
      case 3:
        return (
          <svg width={baseSize} height={baseSize} viewBox="0 0 36 36">
            <rect x="14" y="10" width="8" height="6" fill="currentColor" rx="1"/>
            <rect x="11" y="18" width="6" height="8" fill="currentColor" rx="1"/>
            <rect x="19" y="18" width="6" height="8" fill="currentColor" rx="1"/>
          </svg>
        );
      
      case 4:
        return (
          <svg width={baseSize} height={baseSize} viewBox="0 0 36 36">
            <rect x="12" y="12" width="6" height="6" fill="currentColor" rx="1"/>
            <rect x="20" y="12" width="6" height="6" fill="currentColor" rx="1"/>
            <rect x="12" y="20" width="6" height="6" fill="currentColor" rx="1"/>
            <rect x="20" y="20" width="6" height="6" fill="currentColor" rx="1"/>
          </svg>
        );
      
      case 5:
        return (
          <svg width={baseSize} height={baseSize} viewBox="0 0 36 36">
            <rect x="14" y="8" width="8" height="5" fill="currentColor" rx="1"/>
            <rect x="8" y="15" width="5" height="6" fill="currentColor" rx="1"/>
            <rect x="15" y="15" width="6" height="6" fill="currentColor" rx="1"/>
            <rect x="23" y="15" width="5" height="6" fill="currentColor" rx="1"/>
            <rect x="14" y="23" width="8" height="5" fill="currentColor" rx="1"/>
          </svg>
        );
      
      default: // 6 or more
        return (
          <svg width={baseSize} height={baseSize} viewBox="0 0 36 36">
            <rect x="9" y="6" width="4" height="4" fill="currentColor" rx="1"/>
            <rect x="16" y="6" width="4" height="4" fill="currentColor" rx="1"/>
            <rect x="23" y="6" width="4" height="4" fill="currentColor" rx="1"/>
            <rect x="9" y="12" width="4" height="4" fill="currentColor" rx="1"/>
            <rect x="16" y="12" width="4" height="4" fill="currentColor" rx="1"/>
            <rect x="23" y="12" width="4" height="4" fill="currentColor" rx="1"/>
            <text x="18" y="27" textAnchor="middle" fontSize="12" fill="currentColor" fontWeight="bold">+</text>
          </svg>
        );
    }
  };

  // Helper function to get icons for select options
  const getOptionIcon = (questionId, optionValue) => {
    // Special case for numberOfRooms - show room layout icons
    if (questionId === 'numberOfRooms') {
      return (
        <div className="room-layout-icon">
          {getRoomLayoutSVG(optionValue)}
        </div>
      );
    }

    const iconMap = {
      propertyType: {
        'Lägenhet': faBuilding,
        'Villa': faHome,
        'Radhus': 'double-home', // Special case for two homes
        'Fritidsboende': faUmbrellaBeach,
        'Tomt': faTree,
        'Övrigt': faEllipsisH
      },
      condition: {
        'Mycket bra': faStar,
        'Bra': faCheck,
        'Normalt': faWrench,
        'Behöver renovering': faTools
      },
      ownership: {
        'Ja': faCheck,
        'Nej': faTimes
      }
    };

    const icon = iconMap[questionId]?.[optionValue] || faBuilding;
    
    // Special case for Radhus - show two connected homes
    if (icon === 'double-home') {
      return (
        <div className="double-home-icon">
          <FontAwesomeIcon icon={faHome} />
          <FontAwesomeIcon icon={faHome} />
        </div>
      );
    }
    
    return <FontAwesomeIcon icon={icon} />;
  };

  // Helper function to get option descriptions
  const getOptionDescription = (questionId, optionValue) => {
    const descriptionMap = {};

    return descriptionMap[questionId]?.[optionValue];
  };

  // Helper function to format slider values
  const formatSliderValue = (questionId, value) => {
    if (!value && value !== 0) return '';
    
    switch (questionId) {
      case 'area':
      case 'plotSize':
        return `${value} m²`;
      case 'monthlyFee':
        return isBodexEsHost() ? `${value} €` : `${value} kr`;
      case 'constructionYear':
        return t('valuationChat.yearValue', { value });
      case 'floor':
        return value === 0 || value === '0'
          ? t('valuationChat.floorGround')
          : t('valuationChat.floorValue', { value });
      default:
        return value.toString();
    }
  };

  // Helper function to get slider step
  const getSliderStep = (questionId) => {
    switch (questionId) {
      case 'area':
        return 1; // Step by 1 sqm for precise control
      case 'plotSize':
        return 10; // Changed from 5 to 10 for easier dragging
      case 'monthlyFee':
        // On the Spanish market (bodex.es, bodex.se/spanien,
        // bodex.no/spania, bodex.dk/spanien) the range is 1-1000 €, so a
        // finer step is appropriate. Default Swedish market keeps the 100 kr step.
        return isBodexEsHost() ? 10 : 100; // Changed from 50 to 100 for easier dragging
      case 'constructionYear':
        return 1;
      case 'floor':
        return 1;
      default:
        return 1;
    }
  };

  // Helper function to get slider unit
  const getSliderUnit = (questionId) => {
    switch (questionId) {
      case 'area':
      case 'plotSize':
        return 'm²';
      case 'monthlyFee':
        return isBodexEsHost() ? '€' : 'kr';
      case 'floor':
        // floorValue templates vary in word order across languages (e.g. "Floor {value}"
        // vs "{value}. etasje"), so strip the empty placeholder leftovers to get a clean unit.
        return t('valuationChat.floorValue', { value: '' }).replace(/\.\s*/g, '').trim();
      case 'constructionYear':
        return '';
      default:
        return '';
    }
  };

  // Helper function to get realistic slider ranges
  const getRealisticSliderRange = (questionId) => {
    switch (questionId) {
      case 'area':
        return { min: 20, max: 300, default: 80 }; // Max 300 m² for apartments, houses etc
      case 'floor':
        return { min: 0, max: 20, default: 3 }; // Most apartments are floor 0-20
      case 'monthlyFee':
        // Spanish market (bodex.es, bodex.se/spanien, bodex.no/spania,
        // bodex.dk/spanien) uses EUR and a
        // much lower typical community fee range, so cap the slider at 1-1000 €.
        return isBodexEsHost()
          ? { min: 1, max: 1000, default: 250 }
          : { min: 100, max: 25000, default: 5000 }; // Extended max to 25000 kr
      case 'plotSize':
        return { min: 200, max: 8000, default: 800 }; // Extended max to 8000 m² for larger plots
      case 'constructionYear':
        const currentYear = new Date().getFullYear();
        return { min: 1940, max: currentYear, default: 1980 }; // Most buildings are 1940-current
      default:
        return { min: 0, max: 100, default: 50 };
    }
  };

  const currentQuestion = getCurrentQuestion();
  const validation = validateCurrentAnswer();

  return (
    <div className="modern-valuation-container">
      {/* Progress Section - shown after first question */}
      {currentQuestionIndex > 0 && (
        <div className="modern-progress-section">
          <div className="progress-info">
            <span className="progress-label">{t('valuationChat.progress', { current: currentQuestionIndex + 1, total: getTotalQuestions() })}</span>
            <span className="progress-percentage">{Math.round(calculateProgress())}%</span>
          </div>
          <div className="modern-progress-bar">
            <div 
              className="modern-progress-fill" 
              style={{ width: `${calculateProgress()}%` }}
            />
          </div>
        </div>
      )}

      {/* Question Card */}
      {currentQuestion && !loadingValuation && (
        <div 
          className={`modern-question-card ${isTransitioning ? 'transitioning' : ''}`}
          data-question-id={currentQuestion.id}
        >
          <div className="question-header">
            <div className="question-icon">
              <FontAwesomeIcon icon={currentQuestion.icon} />
            </div>
            <div className="question-text">
              <h2>{currentQuestion.question}</h2>
            </div>
          </div>

          <div className="answer-section">
            {renderInputComponent(currentQuestion)}
            
            {validationError && (
              <div className="modern-validation-error">
                {validationError}
              </div>
            )}
          </div>

          <div className="action-buttons">
            <button
              className={`next-button ${!validation.isValid ? 'disabled' : ''}`}
              onClick={handleSubmitAnswer}
              disabled={!validation.isValid || isSubmitting || hasNavigated}
            >
              {loading ? (
                <Spinner />
              ) : (
                <>
                  {currentQuestionIndex >= getTotalQuestions() - 1 ? (
                    <>
                      <FontAwesomeIcon icon={faCalculator} />
                      {t('valuationChat.getValuation')}
                    </>
                  ) : (
                    <>
                      {t('common.next')}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </>
                  )}
                </>
              )}
            </button>

            {currentQuestionIndex > 0 && (
              <button
                className="previous-button"
                onClick={handlePreviousQuestion}
                disabled={isSubmitting || hasNavigated}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                {t('common.back')}
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Full-page spinner for loading valuation */}
      {loadingValuation && <SpinnerOverlay loadingValuation={true} />}
    </div>
  );
};

export default ValuationChat; 
